import { z } from "zod";

/**
 * Valid analytics provider keys
 */
export const PROVIDER_KEYS = ["segment", "mixpanel", "gtm", "posthog", "customerio"] as const;

export type ProviderKey = (typeof PROVIDER_KEYS)[number];

type AnalyticsBuildEnvironment = Partial<
  Record<"VITE_ANALYTICS_PROVIDER" | "VITE_CUSTOMERIO_CDP_WRITE_KEY" | "VITE_POSTHOG_KEY", string>
>;

/**
 * Zod schema for validating a single provider key
 */
const providerKeySchema = z.enum(PROVIDER_KEYS);

/**
 * Zod schema for parsing the VITE_ANALYTICS_PROVIDER environment variable
 * Accepts comma-separated values like "segment,mixpanel" or "gtm"
 */
const analyticsProviderEnvSchema = z
  .string()
  .optional()
  .transform((val) => {
    if (!val) return [];

    return val
      .split(",")
      .map((p) => p.trim().toLowerCase())
      .filter(Boolean);
  })
  .pipe(z.array(providerKeySchema).default([]));

/**
 * Parses and validates the VITE_ANALYTICS_PROVIDER environment variable
 * Returns a type-safe array of valid provider keys
 *
 * @returns Array of valid provider keys, empty array if none configured or all invalid
 */
export function getAnalyticsConfig(): Array<ProviderKey> {
  const envValue = import.meta.env.VITE_ANALYTICS_PROVIDER;

  try {
    const result = analyticsProviderEnvSchema.parse(envValue);
    return result;
  } catch {
    // In development, warn about invalid providers
    if (import.meta.env.DEV) {
      if (envValue) {
        const invalidKeys = envValue
          .split(",")
          .map((p) => p.trim().toLowerCase())
          .filter((k) => !PROVIDER_KEYS.includes(k as ProviderKey));

        if (invalidKeys.length > 0) {
          console.warn(
            `⚠️ Unknown analytics provider(s): "${invalidKeys.join('", "')}". ` +
              `Available providers: ${PROVIDER_KEYS.join(", ")}`,
          );
        }
      }
    }

    // Return empty array on validation failure (will fallback to NoopAdapter)
    return [];
  }
}

export function assertAnalyticsBuildConfig(env: AnalyticsBuildEnvironment): void {
  const providers = (env.VITE_ANALYTICS_PROVIDER ?? "")
    .split(",")
    .map((provider) => provider.trim().toLowerCase())
    .filter(Boolean);

  if (env.VITE_POSTHOG_KEY?.trim() && !providers.includes("posthog")) {
    throw new Error(
      "VITE_POSTHOG_KEY is configured, but the posthog analytics provider is not enabled in VITE_ANALYTICS_PROVIDER.",
    );
  }

  if (providers.includes("customerio") && !env.VITE_CUSTOMERIO_CDP_WRITE_KEY?.trim()) {
    throw new Error(
      "Customer.io analytics is enabled, but VITE_CUSTOMERIO_CDP_WRITE_KEY is missing from the build environment.",
    );
  }
}
